/**
 * BIH client-marker utility.
 *
 * Wraps Amplify's `generateClient` so every AppSync request carries the
 * `X-BIH-Client` header. The Lambda backend (security.js getClient()) reads
 * this header to attribute requests to either:
 *   - "browser"     — a real human in a browser (default)
 *   - "quick-skill" — the Quick Item Helper skill driving the browser
 *   - "canary"      — CloudWatch Synthetics (set via User-Agent, not header)
 *
 * The Quick skill calls `window.__bihSetClient("quick-skill")` immediately
 * after navigating to the BIH UI, before submitting any work. It can call
 * `window.__bihSetClient("browser")` to revert if the same browser is used
 * for human work afterward (defensive — the page is usually closed instead).
 *
 * Reference: §8.1 schema field `bih.client`; Plan §2.1.6-frontend (Gap G1).
 */

import { generateClient as amplifyGenerateClient } from 'aws-amplify/api';

let currentClient = 'browser';
const CLIENT_VERSION = '2026-05-24';

/**
 * Update the BIH client marker. Used by the Quick skill via window hook,
 * or programmatically by callers that have stronger context.
 * @param {string} value
 */
export function setBihClient(value) {
  if (typeof value !== 'string' || !value.trim()) return;
  currentClient = value.trim().toLowerCase();
}

/**
 * @returns {string} the current client marker
 */
export function getBihClient() {
  return currentClient;
}

/**
 * @returns {object} headers map for AppSync requests
 */
export function getBihHeaders() {
  return {
    'X-BIH-Client': currentClient,
    'X-BIH-Client-Version': CLIENT_VERSION,
  };
}

/**
 * Drop-in replacement for `generateClient()` that always injects the
 * `X-BIH-Client` header. Call site usage is identical:
 *
 *   import { bihGenerateClient } from '../utils/bihClient';
 *   const client = bihGenerateClient();
 *   await client.graphql({ query: ... });
 *
 * Optionally accepts a `tab` option for AppSync-routed Lambda observability.
 * The Lambda reads this via `event.headers['x-bih-tab']` (with allowlist
 * regex `/^[a-z0-9-]{1,40}$/`) to populate `bih.tab` per request:
 *
 *   const client = bihGenerateClient({ tab: 'general' });
 *   const client = bihGenerateClient({ tab: 'generate-rationale' });
 *
 * Reference: invokeBedrock TAB-hardcoding fix (E-today-3); same trust model
 * as `_tab` (IAM gate is the security boundary; tab slug used
 * only for log labeling). The direct-invoke payload no longer carries an
 * `_authToken` — SigV4 via the Cognito Identity Pool is the only auth signal.
 *
 * If callers need to override the headers locally, they can pass their
 * own `headers` to the call — Amplify merges per-call headers with the
 * client-level default.
 */
export function bihGenerateClient(opts = {}) {
  const userHeaders = opts.headers;
  const tab = opts.tab;
  // Strip our custom `tab` field so it isn't passed through to Amplify.
  const cleanOpts = { ...opts };
  delete cleanOpts.tab;
  return amplifyGenerateClient({
    ...cleanOpts,
    headers: () => {
      // Resolve any caller-provided override, then layer the BIH defaults
      // underneath so the BIH marker is always present even if a caller
      // adds an unrelated header.
      const overrides = typeof userHeaders === 'function' ? userHeaders() : (userHeaders || {});
      const tabHeader = (typeof tab === 'string' && tab.trim()) ? { 'X-BIH-Tab': tab.trim().toLowerCase() } : {};
      return { ...getBihHeaders(), ...tabHeader, ...overrides };
    },
  });
}

// Expose the hook for the Quick skill in browser environments.
// In Jest/Node test environments `window` is undefined or jsdom-mocked;
// guard the assignment so SSR-style imports don't blow up.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__bihSetClient = setBihClient;
  // eslint-disable-next-line no-underscore-dangle
  window.__bihGetClient = getBihClient;
}
